import { Grid, useClipboard } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo } from 'react';
import { GridIcon, GridViewIcon, ListIcon } from 'ui-components';
import { LinkButton, SHARE_TYPE } from '~/components/SavedListShareModal';
import useCopyNotification from '~/components/SavedListShareModal/useCopyNotification';
import { FEATURE_FLAGS, VIEW_OPTIONS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetSavedList } from '~/hooks/api/useSavedLists';
import {
  useCreateSharedLink,
  viewTypeToViewOptionsMapper,
} from '~/hooks/api/useSavedLists/useCreateSharedLink';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import {
  VIEW_TYPE,
  useActiveView,
} from '~/screens/MyAgency/hooks/useActiveView';

interface Props {
  shareLink: string;
  listId: string;
  audienceFiltersHidden: boolean;
  onGenerateLink?: (enabledControls: string[]) => string | Promise<string>;
  isEmailRequired: boolean;
}

const viewToTrackIdMap = {
  [VIEW_TYPE.card]: 'cards',
  [VIEW_TYPE.list]: 'list',
  [VIEW_TYPE.stacked]: 'gallery',
};
const viewToIconMap = {
  [VIEW_TYPE.card]: GridViewIcon,
  [VIEW_TYPE.list]: ListIcon,
  [VIEW_TYPE.stacked]: GridIcon,
};

export const CopyLinkButtons: React.FC<Props> = ({
  shareLink,
  listId,
  audienceFiltersHidden,
  onGenerateLink,
  isEmailRequired,
}) => {
  const { handleSuccess, handleError } = useCopyNotification();
  const { mutateAsync: createSharedLink } = useCreateSharedLink();
  const { data: list } = useGetSavedList({ id: listId });
  const { activeView } = useActiveView();
  const cardViewFF = useFeatureFlag(FEATURE_FLAGS.foamCollapsedView);

  const {
    onCopy: onCopyEmailTemplate,
    value: emailTemplate,
    setValue: setLink,
  } = useClipboard('');

  useEffect(() => {
    if (!emailTemplate) return;
    onCopyEmailTemplate();
    setLink('');
  }, [onCopyEmailTemplate, emailTemplate, setLink]);

  const handleGenerateShareLink = useCallback(
    async (view: VIEW_OPTIONS) => {
      try {
        const url = await createSharedLink({
          sharedLink: shareLink,
          onLinkClick: onGenerateLink,
          listId,
          isEmailRequired,
          options: {
            view,
            audienceFiltersHidden,
          },
        });
        if (url) {
          setLink(url);
        }
        handleSuccess(SHARE_TYPE.link, view);
        {
          listId &&
            trackEvent('foam-shared-link-properties', {
              category: list?.category || '',
              isEmailRequired: isEmailRequired ? 'yes' : 'no',
              areAudienceFiltersHidden: audienceFiltersHidden ? 'yes' : 'no',
              listViewType: view,
              listName: list?.name || '',
            });
        }
      } catch {
        handleError();
      }
    },
    [
      audienceFiltersHidden,
      listId,
      list?.name,
      list?.category,
      isEmailRequired,
      createSharedLink,
      handleError,
      handleSuccess,
      onGenerateLink,
      setLink,
      shareLink,
    ],
  );

  const shareCopyLinkClick = useCallback(() => {
    handleGenerateShareLink(viewTypeToViewOptionsMapper[activeView]);
  }, [activeView, handleGenerateShareLink]);

  const copyButtonTrackId = useMemo(
    () => `foam-share-list-${viewToTrackIdMap[activeView]}-view`,
    [activeView],
  );

  const activeViewIcon = useMemo(() => viewToIconMap[activeView], [activeView]);

  return (
    <>
      {cardViewFF === 'enabled' ? (
        <Grid templateColumns={{ base: '1fr' }} gap={2}>
          <LinkButton
            title="Copy link"
            track-id={copyButtonTrackId}
            onClick={shareCopyLinkClick}
            icon={activeViewIcon}
          />
        </Grid>
      ) : (
        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={2}>
          <LinkButton
            title="Copy list link"
            track-id="foam-share-list-list-view"
            onClick={() => handleGenerateShareLink(VIEW_OPTIONS.list)}
            icon={ListIcon}
          />
          <LinkButton
            title="Copy gallery link"
            track-id="foam-share-list-gallery-view"
            onClick={() => handleGenerateShareLink(VIEW_OPTIONS.stacked)}
            icon={GridIcon}
          />
        </Grid>
      )}
    </>
  );
};
